import { useState } from 'react';
import { 
  XMarkIcon, 
  BellIcon, 
  UserGroupIcon, 
  Cog6ToothIcon,
  EnvelopeIcon,
  DevicePhoneMobileIcon,
  FolderIcon,
  CheckIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import type { Departamento, Colaborador } from '../../types';

// Tipo estendido para suportar novos campos
interface DepartamentoExtendido extends Departamento {
  membros?: string[];
  notificacoesEmail?: {
    habilitado: boolean;
    destinatarios: string[];
  };
  notificacoesWhatsapp?: {
    habilitado: boolean;
    destinatarios: string[];
  };
  cor?: string;
  ativo?: boolean;
}

interface ConfiguracaoDepartamentoProps {
  departamento: Departamento;
  colaboradores: Colaborador[];
  onSave: (departamento: Departamento) => void;
  onClose: () => void;
  onDelete?: (departamentoId: string) => void;
}

export const ConfiguracaoDepartamento: React.FC<ConfiguracaoDepartamentoProps> = ({
  departamento: deptOriginal,
  colaboradores,
  onSave,
  onClose,
  onDelete
}) => {
  // Cast para tipo estendido
  const departamento = deptOriginal as DepartamentoExtendido;
  
  const [nome, setNome] = useState(departamento.nome);
  const [descricao, setDescricao] = useState(departamento.descricao || '');
  const [responsavelId, setResponsavelId] = useState<string>(departamento.responsavel || '');
  const [membros, setMembros] = useState<string[]>(departamento.membros || []);
  
  // Notificações por Email
  const [notificacoesEmailHabilitado, setNotificacoesEmailHabilitado] = useState<boolean>(
    departamento.notificacoesEmail?.habilitado ?? false
  );
  const [notificacoesEmailPara, setNotificacoesEmailPara] = useState<string[]>(
    departamento.notificacoesEmail?.destinatarios || []
  );
  
  // Notificações por WhatsApp
  const [notificacoesWhatsappHabilitado, setNotificacoesWhatsappHabilitado] = useState<boolean>(
    departamento.notificacoesWhatsapp?.habilitado ?? false
  );
  const [notificacoesWhatsappPara, setNotificacoesWhatsappPara] = useState<string[]>(
    departamento.notificacoesWhatsapp?.destinatarios || []
  );
  
  // Configurações gerais
  const [corPasta, setCorPasta] = useState(departamento.cor || 'bg-blue-500');
  const [ativo, setAtivo] = useState(departamento.ativo ?? true);
  
  // Abas
  const [abaAtiva, setAbaAtiva] = useState<'geral' | 'membros' | 'notificacoes'>('geral');
  
  // Filtrar colaboradores do departamento
  const colaboradoresDoDepartamento = colaboradores.filter(c => 
    String(c.departamentoId) === String(departamento.id) && c.status === 'ativo'
  );
  
  // Todos colaboradores ativos
  const colaboradoresAtivos = colaboradores.filter(c => c.status === 'ativo');

  const cores = [
    { nome: 'Azul', valor: 'bg-blue-500' },
    { nome: 'Verde', valor: 'bg-green-500' },
    { nome: 'Vermelho', valor: 'bg-red-500' },
    { nome: 'Laranja', valor: 'bg-orange-500' },
    { nome: 'Roxo', valor: 'bg-purple-500' },
    { nome: 'Rosa', valor: 'bg-pink-500' },
    { nome: 'Índigo', valor: 'bg-indigo-500' },
    { nome: 'Teal', valor: 'bg-teal-500' },
    { nome: 'Ciano', valor: 'bg-cyan-500' },
    { nome: 'Amarelo', valor: 'bg-yellow-500' },
  ];

  const handleSave = () => {
    const departamentoAtualizado = {
      ...departamento,
      nome,
      descricao,
      responsavel: responsavelId,
      membros,
      cor: corPasta,
      ativo,
      notificacoesEmail: {
        habilitado: notificacoesEmailHabilitado,
        destinatarios: notificacoesEmailPara
      },
      notificacoesWhatsapp: {
        habilitado: notificacoesWhatsappHabilitado,
        destinatarios: notificacoesWhatsappPara
      },
      updatedAt: new Date()
    } as unknown as Departamento;
    onSave(departamentoAtualizado);
  };

  const toggleMembroNotificacaoEmail = (colaboradorId: string) => {
    setNotificacoesEmailPara(prev => 
      prev.includes(colaboradorId) 
        ? prev.filter(id => id !== colaboradorId)
        : [...prev, colaboradorId]
    );
  };

  const toggleMembroNotificacaoWhatsapp = (colaboradorId: string) => {
    setNotificacoesWhatsappPara(prev => 
      prev.includes(colaboradorId) 
        ? prev.filter(id => id !== colaboradorId)
        : [...prev, colaboradorId]
    );
  };

  const toggleMembro = (colaboradorId: string) => {
    setMembros(prev => 
      prev.includes(colaboradorId) 
        ? prev.filter(id => id !== colaboradorId)
        : [...prev, colaboradorId]
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg ${corPasta} flex items-center justify-center shadow-md`}>
              <FolderIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Configurar Departamento</h2>
              <p className="text-xs text-gray-500">{departamento.nome}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Abas */}
        <div className="flex border-b border-gray-200 px-6 bg-gray-50">
          <button
            onClick={() => setAbaAtiva('geral')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              abaAtiva === 'geral'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Cog6ToothIcon className="h-4 w-4" />
            Geral
          </button>
          <button
            onClick={() => setAbaAtiva('membros')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              abaAtiva === 'membros'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <UserGroupIcon className="h-4 w-4" />
            Membros
            {membros.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full">
                {membros.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setAbaAtiva('notificacoes')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              abaAtiva === 'notificacoes'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <BellIcon className="h-4 w-4" />
            Notificações
          </button>
        </div>

        {/* Conteúdo */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Aba Geral */}
          {abaAtiva === 'geral' && (
            <div className="space-y-5">
              {/* Nome */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do Departamento *
                </label>
                <input
                  type="text"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                  placeholder="Ex: Recursos Humanos"
                />
              </div>

              {/* Descrição */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <textarea
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm resize-none"
                  rows={3}
                  placeholder="Descreva a função deste departamento..."
                />
              </div>

              {/* Responsável */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Responsável Principal
                </label>
                <select
                  value={responsavelId}
                  onChange={(e) => setResponsavelId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                >
                  <option value="">Selecione um responsável</option>
                  {colaboradoresAtivos.map(colab => (
                    <option key={colab.id} value={colab.id}>
                      {colab.nome} {colab.cargo ? `(${colab.cargo})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Cor da Pasta */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cor do Departamento
                </label>
                <div className="flex flex-wrap gap-2">
                  {cores.map(cor => (
                    <button
                      key={cor.valor}
                      onClick={() => setCorPasta(cor.valor)}
                      className={`w-8 h-8 rounded-lg ${cor.valor} flex items-center justify-center transition-all ${
                        corPasta === cor.valor 
                          ? 'ring-2 ring-offset-2 ring-indigo-500 scale-110' 
                          : 'hover:scale-105'
                      }`}
                      title={cor.nome}
                    >
                      {corPasta === cor.valor && (
                        <CheckIcon className="h-4 w-4 text-white" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Status Ativo */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-700">Departamento Ativo</p>
                  <p className="text-xs text-gray-500">Departamentos inativos não aparecem na lista</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={ativo}
                    onChange={(e) => setAtivo(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                </label>
              </div>
            </div>
          )}

          {/* Aba Membros */}
          {abaAtiva === 'membros' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-700">
                  <strong>Dica:</strong> Selecione os colaboradores que fazem parte deste departamento. 
                  Eles terão acesso aos procedimentos e receberão notificações configuradas.
                </p>
              </div>

              {/* Busca rápida */}
              <input
                type="text"
                placeholder="Buscar colaborador..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
              />

              {/* Lista de colaboradores */}
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {colaboradoresAtivos.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">
                    Nenhum colaborador cadastrado
                  </p>
                ) : (
                  colaboradoresAtivos.map(colab => {
                    const isMembro = membros.includes(colab.id);
                    const isResponsavel = responsavelId === colab.id;
                    const jaNoDepartamento = String(colab.departamentoId) === String(departamento.id);
                    
                    return (
                      <label
                        key={colab.id}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          isMembro || jaNoDepartamento
                            ? 'bg-indigo-50 border border-indigo-200'
                            : 'bg-gray-50 hover:bg-gray-100 border border-transparent'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isMembro || jaNoDepartamento}
                          onChange={() => toggleMembro(colab.id)}
                          className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        />
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                          isMembro || jaNoDepartamento ? 'bg-indigo-500' : 'bg-gray-400'
                        }`}>
                          {colab.nome?.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{colab.nome}</p>
                          <p className="text-xs text-gray-500">{colab.cargo || colab.email}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {isResponsavel && (
                            <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                              Responsável
                            </span>
                          )}
                          {jaNoDepartamento && !isMembro && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                              No depto.
                            </span>
                          )}
                        </div>
                      </label>
                    );
                  })
                )}
              </div>

              <div className="text-xs text-gray-500 text-center pt-2">
                {membros.length + colaboradoresDoDepartamento.length} membro(s) selecionado(s)
              </div>
            </div>
          )}

          {/* Aba Notificações */}
          {abaAtiva === 'notificacoes' && (
            <div className="space-y-6">
              {/* Notificações por Email */}
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="flex items-center justify-between p-4 bg-gray-50 border-b">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <EnvelopeIcon className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Notificações por E-mail</p>
                      <p className="text-xs text-gray-500">Novos procedimentos e atualizações</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notificacoesEmailHabilitado}
                      onChange={(e) => setNotificacoesEmailHabilitado(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
                
                {notificacoesEmailHabilitado && (
                  <div className="p-4 space-y-3">
                    <p className="text-xs text-gray-500 mb-2">Selecione quem receberá notificações:</p>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {colaboradoresAtivos.length === 0 ? (
                        <p className="text-xs text-gray-400 text-center py-2">
                          Nenhum colaborador ativo encontrado
                        </p>
                      ) : (
                        colaboradoresAtivos.map(colab => (
                          <label
                            key={colab.id}
                            className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                              notificacoesEmailPara.includes(colab.id)
                                ? 'bg-blue-50'
                                : 'hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={notificacoesEmailPara.includes(colab.id)}
                              onChange={() => toggleMembroNotificacaoEmail(colab.id)}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">{colab.nome}</span>
                            {colab.email && (
                              <span className="text-xs text-gray-400 ml-auto">{colab.email}</span>
                            )}
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Notificações por WhatsApp */}
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="flex items-center justify-between p-4 bg-gray-50 border-b">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                      <DevicePhoneMobileIcon className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Notificações por WhatsApp</p>
                      <p className="text-xs text-gray-500">Alertas instantâneos no celular</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notificacoesWhatsappHabilitado}
                      onChange={(e) => setNotificacoesWhatsappHabilitado(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                  </label>
                </div>
                
                {notificacoesWhatsappHabilitado && (
                  <div className="p-4 space-y-3">
                    <p className="text-xs text-gray-500 mb-2">Selecione quem receberá notificações:</p>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {colaboradoresAtivos.length === 0 ? (
                        <p className="text-xs text-gray-400 text-center py-2">
                          Nenhum colaborador ativo encontrado
                        </p>
                      ) : (
                        colaboradoresAtivos.map(colab => (
                          <label
                            key={colab.id}
                            className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                              notificacoesWhatsappPara.includes(colab.id)
                                ? 'bg-green-50'
                                : 'hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={notificacoesWhatsappPara.includes(colab.id)}
                              onChange={() => toggleMembroNotificacaoWhatsapp(colab.id)}
                              className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                            />
                            <span className="text-sm text-gray-700">{colab.nome}</span>
                            {colab.telefone && (
                              <span className="text-xs text-gray-400 ml-auto">{colab.telefone}</span>
                            )}
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Dica sobre notificações */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-xs text-yellow-700">
                  <strong>Quando são enviadas?</strong><br />
                  • Novo procedimento criado no departamento<br />
                  • Procedimento atualizado ou editado<br />
                  • Membro adicionado ou removido
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div>
            {onDelete && (
              <button
                onClick={() => {
                  if (window.confirm(`Tem certeza que deseja excluir o departamento "${departamento.nome}"?`)) {
                    onDelete(departamento.id);
                  }
                }}
                className="flex items-center gap-2 px-3 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors text-sm"
              >
                <TrashIcon className="h-4 w-4" />
                Excluir
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm font-medium"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={!nome.trim()}
              className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
            >
              Salvar Configurações
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfiguracaoDepartamento;
